import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';

import css from './style.scss';

// Hover card behind the match pill — Figma 3:262 ("WHY THIS MATCH?").
//
// A dedicated tooltip rather than the shared app/components/Tooltip: that one
// always draws an arrow and carries its own shadow/padding, while this design
// has no arrow and a specific soft two-layer shadow — matching it through that
// component would mean fighting its stylesheet. Kept portal-based, to the same
// #tooltip-root that component uses, because the results list is `overflow-y:
// auto` and a plain absolutely-positioned card would be clipped at the scroll
// edge.
//
// Shows the "WHY THIS MATCH?" title over the generated description (no strategy
// line / sub-pills) — the description is built by data/investors'
// formatInvestorMatchSummary.

// Gap the design floats the card above its trigger.
const GAP = 8;

const MatchTooltip = ({ text, children }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    // Anchored just below the pill, centred on it; the card centres itself
    // horizontally in CSS (translateX(-50%)).
    setCoords({
      top: rect.bottom + window.scrollY + GAP,
      left: rect.left + window.scrollX + rect.width / 2,
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    place();
    // Scroll doesn't bubble, so capture to catch any scrolling ancestor (the
    // results list), not just the window.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);

    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  const root = typeof document === 'undefined' ? null : document.getElementById('tooltip-root');

  return (
    <span
      ref={triggerRef}
      className={css.matchTrigger}
      tabIndex={0}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {children}
      {open && root
        ? ReactDOM.createPortal(
            <div
              className={css.matchCard}
              style={{ top: coords.top, left: coords.left }}
              role="tooltip"
            >
              <p className={css.matchCard__title}>WHY THIS MATCH?</p>
              <p className={css.matchCard__body}>{text}</p>
            </div>,
            root
          )
        : null}
    </span>
  );
};

export default MatchTooltip;
